// Ubicación (documento 07, "GPS"). Implementación web con watchPosition: funciona con la app en pantalla.
// En fase 2, con Capacitor, se reemplaza por un plugin de ubicación en segundo plano con la misma interfaz.
import { haversineM } from '@reparto/shared/nucleo';
import { api, SinRed } from '../datos/api';
import { db } from '../datos/db';

export interface Lectura {
  lat: number;
  lng: number;
  precision_m: number;
  velocidad_kmh?: number;
  rumbo?: number;
  hora: number;
}

export interface ParametrosGps {
  movilId: string;
  intervaloMovimientoS: number;
  intervaloQuietoS: number;
  precisionMaximaM: number;
  envioLoteS: number;
}

export interface ServicioGps {
  ultima(): Lectura | null;
  /** Lectura puntual para una entrega (usa la última si es reciente). */
  actual(maximoMs?: number): Promise<Lectura | null>;
  iniciarSeguimiento(p: ParametrosGps): void;
  detenerSeguimiento(): void;
  permiso(): Promise<'concedido' | 'denegado' | 'preguntar' | 'no_disponible'>;
}

const aLectura = (p: GeolocationPosition): Lectura => ({
  lat: p.coords.latitude,
  lng: p.coords.longitude,
  precision_m: Math.round(p.coords.accuracy),
  ...(p.coords.speed !== null && p.coords.speed >= 0 && { velocidad_kmh: Math.round(p.coords.speed * 3.6 * 10) / 10 }),
  ...(p.coords.heading !== null && !Number.isNaN(p.coords.heading) && { rumbo: Math.round(p.coords.heading) }),
  hora: p.timestamp,
});

class GpsWeb implements ServicioGps {
  private ultimaLectura: Lectura | null = null;
  private vigilancia: number | null = null;
  private envio: ReturnType<typeof setInterval> | null = null;
  private guardada: Lectura | null = null;
  private params: ParametrosGps | null = null;

  ultima() {
    return this.ultimaLectura;
  }

  async permiso() {
    if (!('geolocation' in navigator)) return 'no_disponible' as const;
    try {
      const p = await navigator.permissions.query({ name: 'geolocation' });
      return p.state === 'granted' ? 'concedido' : p.state === 'denied' ? 'denegado' : 'preguntar';
    } catch {
      return 'preguntar' as const;
    }
  }

  actual(maximoMs = 30_000): Promise<Lectura | null> {
    if (this.ultimaLectura && Date.now() - this.ultimaLectura.hora < maximoMs)
      return Promise.resolve(this.ultimaLectura);
    if (!('geolocation' in navigator)) return Promise.resolve(null);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (p) => {
          this.ultimaLectura = aLectura(p);
          resolve(this.ultimaLectura);
        },
        () => resolve(this.ultimaLectura),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: maximoMs },
      );
    });
  }

  iniciarSeguimiento(p: ParametrosGps) {
    this.params = p;
    if (this.vigilancia !== null || !('geolocation' in navigator)) return;
    this.vigilancia = navigator.geolocation.watchPosition(
      (pos) => void this.recibir(aLectura(pos)),
      () => undefined,
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 20_000,
      },
    );
    this.envio = setInterval(() => void this.enviar(), p.envioLoteS * 1000);
  }

  detenerSeguimiento() {
    if (this.vigilancia !== null) navigator.geolocation.clearWatch(this.vigilancia);
    if (this.envio) clearInterval(this.envio);
    this.vigilancia = null;
    this.envio = null;
    void this.enviar();
  }

  /** Filtro: descartar imprecisas; guardar cada N s en movimiento (si se movió más de 15 m) o cada M s quieto. */
  private async recibir(l: Lectura) {
    this.ultimaLectura = l;
    const p = this.params;
    if (!p || l.precision_m > p.precisionMaximaM) return;
    const g = this.guardada;
    const transcurrido = g ? (l.hora - g.hora) / 1000 : Infinity;
    const movido = g ? haversineM(g, l) : Infinity;
    const enMovimiento = (l.velocidad_kmh ?? 0) > 5 || movido > 15;
    const toca = enMovimiento
      ? transcurrido >= p.intervaloMovimientoS && movido > 15
      : transcurrido >= p.intervaloQuietoS;
    if (toca) {
      this.guardada = l;
      await db.posiciones.add({
        movil_id: p.movilId,
        lat: l.lat,
        lng: l.lng,
        precision_m: l.precision_m,
        velocidad_kmh: l.velocidad_kmh,
        rumbo: l.rumbo,
        hora_dispositivo: new Date(l.hora).toISOString(),
        bateria_pct: await bateria(),
      });
    }
  }

  /** Envía lo acumulado; sin señal queda guardado con su hora original. */
  async enviar() {
    const lote = await db.posiciones.orderBy('id').limit(500).toArray();
    if (!lote.length) return;
    const porMovil = new Map<string, typeof lote>();
    for (const x of lote) porMovil.set(x.movil_id, [...(porMovil.get(x.movil_id) ?? []), x]);
    for (const [movilId, ps] of porMovil) {
      try {
        await api('/posiciones/lote', {
          cuerpo: {
            movil_id: movilId,
            posiciones: ps.map((x) => ({
              lat: x.lat,
              lng: x.lng,
              precision_m: x.precision_m,
              ...(x.velocidad_kmh !== undefined && { velocidad_kmh: x.velocidad_kmh }),
              ...(x.rumbo !== undefined && { rumbo: x.rumbo }),
              hora_dispositivo: x.hora_dispositivo,
              ...(x.bateria_pct !== undefined && { bateria_pct: x.bateria_pct }),
            })),
          },
        });
        await db.posiciones.bulkDelete(ps.map((x) => x.id!));
      } catch (e) {
        if (e instanceof SinRed) return;
        // Un lote inválido no debe trabar los siguientes.
        await db.posiciones.bulkDelete(ps.map((x) => x.id!));
      }
    }
  }
}

async function bateria(): Promise<number | undefined> {
  try {
    const b = await (navigator as Navigator & { getBattery?: () => Promise<{ level: number }> }).getBattery?.();
    return b ? Math.round(b.level * 100) : undefined;
  } catch {
    return undefined;
  }
}

export const gps: ServicioGps = new GpsWeb();
